import { FCX } from "@datassential/platform-ui-lib";
import HomeHeader from "@pages/Home/components/HomeHeader/HomeHeader";
import { useEffect, useId, useMemo } from "react";
import HomeSubHeader from "@pages/Home/components/HomeHeader/HomeSubHeader";
import SelectAmong from "@vms/SelectAmong";
import { useHomeContext, useHomeSearchSettingsContext } from "@pages/Home/HomeContext";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import { TableColumnsContext } from "@components/Table/TableColumns";
import WindowScrollerTable from "@components/Table/WindowScrollerTable";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";
import HomeActionsBar from "@pages/Home/components/HomeActionsBar/HomeActionsBar";
import { useTrendSummaryTableConfig } from "@pages/Trend/components/TrendSummary/TrendSummaryTableConfig";
import { TrendSummaryItem, useTrendSummary } from "@api/useTrendSummary";
import { KeywordBase } from "@api/useGetKeywordBaseData";
import HeatScoreTooltip from "@vms/HeatScoreTooltip";
import DataReflectLabel from "@vms/DataReflectLabel";
import { TREND_SUMMARY_METRICS_ORDER } from "@models/Metric";

type TrendSummaryWithKeywordBase = TrendSummaryItem & KeywordBase;

const TrendView: FCX = () => {
    const heatScoreTooltipId = useId();
    const { search } = useHomeContext();
    const { data: homeSearchData, resetSearch } = useHomeSearchSettingsContext();

    const { amongPayload } = useSelectAmongContext();
    const { data: rawData, isLoading } = useTrendSummary(amongPayload);

    const isSearchApplied = useMemo(
        () => search.trim().length > 0,
        [search]
    );

    const { getFinalData, dataForSorting } = useFilteredKeywordsData(rawData as TrendSummaryWithKeywordBase[], search);

    const mappedDataForSorting = useMemo(
        () => (dataForSorting || []).map(i => ({
            ...i,
            ...i.metrics.reduce(
                (acc, metric, index) => ({
                    ...acc,
                    [`metric${index}`]: metric.numValue,
                    [`metric${index}Change`]: metric.changeNumValue,
                }),
                {}
            ),
        })),
        [dataForSorting]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(
        mappedDataForSorting,
        'name',
        Sort.Asc,
    );

    useEffect(() => {
        if (homeSearchData?.sortByMetric) {
            const metricIndex = TREND_SUMMARY_METRICS_ORDER.findIndex(i => i === homeSearchData?.sortByMetric);

            setSortingKey(`metric${metricIndex}` as keyof TrendSummaryItem);
            setSortingDirection(homeSearchData?.sortingDirection);
        }

        else if (homeSearchData?.sortByHeatScore) {
            setSortingKey("heatScoreNum");
            setSortingDirection(homeSearchData?.sortingDirection);
        }
    }, [homeSearchData, setSortingDirection, setSortingKey]);

    const data = useMemo(
        () => getFinalData(sortedData as TrendSummaryWithKeywordBase[]),
        [getFinalData, sortedData]
    );

    const tableConfig = useTrendSummaryTableConfig(data, heatScoreTooltipId, isSearchApplied);

    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig],
    );

    const tableSortingProviderValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                { id: "name", name: "name", defaultDirection: Sort.Asc },
                { id: "macStage", name: "macStage", defaultDirection: Sort.Asc },
                { id: "baseCount", name: "baseCount", },
                { id: "metric0", name: "metric0", },
                { id: "metric0Change", name: "metric0Change", },
                { id: "metric1", name: "metric1", },
                { id: "metric1Change", name: "metric1Change", },
                { id: "metric2", name: "metric2", },
                { id: "metric2Change", name: "metric2Change", },
                { id: "metric3", name: "metric3", },
                { id: "metric3Change", name: "metric3Change", },
                { id: "heatScoreNum", name: "heatScoreNum" },
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof TrendSummaryItem);
                setSortingDirection(direction);
            },
        }),
        [setSortingDirection, setSortingKey, sortingDirection, sortingKey]
    );

    const copyTableContent = useCopyTableDataWithFilters(tableConfig, data.length);

    return (
        <>
            <HomeHeader resultCount={dataForSorting.length} isLoading={isLoading}/>
            <HomeSubHeader>
                <SelectAmong
                    label="AMONG:"
                    useGroupsLimit
                    useCoExclusions
                    onApply={resetSearch}
                />
                <span className="HomeTrendView__data-reflect-label"><DataReflectLabel /></span>
            </HomeSubHeader>
            <TableSortingContext.Provider value={tableSortingProviderValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <WindowScrollerTable
                        rowsCount={isLoading ? 0 : data.length}
                        headHeight={52}
                        rowHeight={42}
                        isLoading={isLoading}
                        showFallback
                    >
                        <AsideButtons>
                            <CopyTableButton
                                data={copyTableContent}
                            />
                        </AsideButtons>
                    </WindowScrollerTable>
                    <HeatScoreTooltip id={heatScoreTooltipId}/>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
            <HomeActionsBar exportPayload={{}}/>
        </>
    );
};

export default TrendView;